import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { isEmpty } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { CompanyMeasurementDateName } from 'api';
import { FUND_TYPE } from 'common/constants/pageTypes';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import ExcelExportContext from 'context/ExcelExportContext';
import BulkActionsContentWrapper from 'layouts/Main/components/BulkActionsLedger/components/BulkActionsContentWrapper/BulkActionsContentWrapper';
import SelectFundMeasurementDate from 'layouts/Main/components/BulkActionsLedger/components/SelectFundMeasurementDate/SelectFundMeasurementDate';
import { FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG } from 'pages/Firms/common/constants/firmSummary';
import { useFirmMeasurementDateFilter } from 'pages/Firms/hooks/useFirmMeasurementDateFilter';
import { getFundInvestments, getFundSummary } from 'services/hooks/funds';
import { Logger } from 'utilities';
import { useBaseExcelExportForm, useBaseExcelExportState } from '../BaseExcelExportDialog';
import { useExcelExportLogic } from '../BaseExcelExportDialog/useExcelExportLogic';
import { BaseDialogProps, ExcelExportContextType } from '../types';

const INFO_TOAST_DURATION_MS = 5000;

export interface FundExcelExportDialogProps extends BaseDialogProps {
  fundId: number;
}

export const FundExcelExportDialog = ({ open, onClose, fundId }: FundExcelExportDialogProps) => {
  const [{ firmInfo, fundList }] = useStore() as unknown as UseStoreValues;
  const fundInfo = useMemo(() => fundList?.find(fund => fund.id === fundId) ?? null, [fundId, fundList]);

  const { isLoading: isExportLoading } = useContext(ExcelExportContext) as unknown as ExcelExportContextType;

  const {
    selectedMeasurementDate: selectedDateInFirmLayout,
    isLoadingMeasurementDate,
    companyMeasurementDates,
  } = useFirmMeasurementDateFilter();

  const [selectedReportDate, setSelectedReportDate] = useState<CompanyMeasurementDateName | null>(null);
  const [hasFundSummary, setHasFundSummary] = useState(false);
  const [hasFundInvestments, setHasFundInvestments] = useState(false);
  const [formulated, setFormulated] = useState(true);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const { formValues, updateFormValues } = useBaseExcelExportForm();
  const { isLoading, setIsLoading, setDataLoadError, resetState } = useBaseExcelExportState();

  const isVirtualRecentDateSelectedForReport = useMemo<boolean>(
    () => selectedReportDate?.date === FIRM_SUMMARY_MOST_RECENT_MEASUREMENT_DATE_SLUG,
    [selectedReportDate]
  );

  useEffect(() => {
    if (selectedDateInFirmLayout) {
      setSelectedReportDate(selectedDateInFirmLayout);
    }
  }, [selectedDateInFirmLayout]);

  const { generateReport } = useExcelExportLogic({
    pageType: FUND_TYPE,
    firmInfo,
    companyInfo: null,
    fundInfo,
    currentMeasurementDateId: isVirtualRecentDateSelectedForReport ? null : selectedReportDate?.md_id,
    formValues,
    setErrors,
  });

  useEffect(() => {
    if (!open) {
      setErrors({});
      resetState();
      return;
    }
    setFormulated(true);
  }, [open, resetState]);

  useEffect(() => {
    updateFormValues({
      export_fund_summary: hasFundSummary,
      export_fund_investments: hasFundInvestments,
      export_not_formulated: !formulated,
      export_formulated_and_print_ready: false,
    });
  }, [formulated, hasFundInvestments, hasFundSummary, updateFormValues]);

  useEffect(() => {
    const loadFundExportData = async () => {
      if (!open || !fundId || !selectedReportDate || isLoadingMeasurementDate) {
        return;
      }

      setIsLoading(true);
      setDataLoadError(null);

      try {
        const measurementDateSlug = selectedReportDate.slug;
        const measurementDate = selectedReportDate;

        const [fundSummary, fundInvestments] = await Promise.all([
          getFundSummary({ fundId, measurementDateSlug, measurementDate }),
          getFundInvestments({
            fundId,
            measurementDateSlug,
            measurementDate,
            checkForExport: true,
          }),
        ]);

        const summaryAvailable = !isEmpty(fundSummary?.companies);
        const investmentsAvailable = !isEmpty(fundInvestments?.companies);

        setHasFundSummary(summaryAvailable);
        setHasFundInvestments(investmentsAvailable);

        if (!summaryAvailable && !investmentsAvailable) {
          enqueueSnackbar('No data available to export', {
            variant: 'info',
            autoHideDuration: INFO_TOAST_DURATION_MS,
          });
        }
      } catch (error) {
        Logger.error('Failed to load fund export data:', error);
        setDataLoadError('Failed to load fund export data');
        setHasFundSummary(false);
        setHasFundInvestments(false);
      } finally {
        setIsLoading(false);
      }
    };

    loadFundExportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fundId, selectedReportDate, isLoadingMeasurementDate]);

  const handleExport = useCallback(async () => {
    await generateReport(formValues);
  }, [formValues, generateReport]);

  const toggleFundSummary = useCallback(() => {
    updateFormValues({ export_fund_summary: !formValues.export_fund_summary });
  }, [formValues.export_fund_summary, updateFormValues]);

  const toggleFundInvestments = useCallback(() => {
    updateFormValues({ export_fund_investments: !formValues.export_fund_investments });
  }, [formValues.export_fund_investments, updateFormValues]);

  const hasAtLeastOneReport =
    (hasFundSummary && formValues.export_fund_summary) ||
    (hasFundInvestments && formValues.export_fund_investments);

  const isExportDisabled =
    isLoading || isExportLoading || !fundInfo || !isEmpty(errors) || !hasAtLeastOneReport;

  const appBarProps = useMemo(
    () => ({
      onClose,
      onSave: handleExport,
      isButtonDisabled: isExportDisabled,
      title: fundInfo?.name ? `Excel Export — ${fundInfo.name}` : 'Excel Export',
      subtitle: 'Export fund summary and schedule of investments',
      actionLabel: 'EXPORT',
    }),
    [fundInfo?.name, handleExport, isExportDisabled, onClose]
  );

  return (
    <BulkActionsContentWrapper open={open} onClose={onClose} isLoading={isLoading} appBarProps={appBarProps}>
      <SelectFundMeasurementDate
        selectedFundMeasurementDateState={{
          value: selectedReportDate,
          setter: setSelectedReportDate,
        }}
        uniqueFundMeasurementDatesByDate={companyMeasurementDates}
        label="Measurement Date"
      />
      <FormGroup sx={{ marginLeft: '25px', marginTop: '16px' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formValues.export_fund_summary}
              onChange={toggleFundSummary}
              disabled={!hasFundSummary}
            />
          }
          label="Fund Summary"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={formValues.export_fund_investments}
              onChange={toggleFundInvestments}
              disabled={!hasFundInvestments}
            />
          }
          label="Schedule of Investments"
        />
        <FormControlLabel
          control={
            <Checkbox checked={formulated} onChange={event => setFormulated(event.target.checked)} />
          }
          label="Formulated"
        />
      </FormGroup>
    </BulkActionsContentWrapper>
  );
};
